// src/encounter/care-pathway-rail.tsx
//
// The pre-encounter's RIGHT column, and the care-pathway READ every host of the pathway shares.
//
// Showcase look (Antonin, 15 Oct: "right panel of the encounter is unusable. make it clean
// collapsible sections. Check proto for inspiration"): the column is no longer the Signal DS
// `CarePathwayRail` (a Timeline · Note · Orders · Edit tab row, two of them permanently disabled
// here, and a header of four loose controls). It is one scrolling column of collapsible sections
// (`panel-section.tsx`), the same column the started encounter shows (`encounter-drawer.tsx`):
//   · Care pathway — `CarePathwayTimeline` fed by `CarePathwayFeed` below;
//   · Note — the generated note the host passes, when it passes one.
// Orders and Clinical edits are not drawn before Start: nothing feeds them here (the DS rail drew
// them as disabled tabs). The DS organism is not edited; it is simply no longer mounted here.
//
// THE FEED IS REAL (PROD-738). Given an `appointmentId` this adapter reads
// GET workflows/appointments/{id}/history via `useAppointmentWorkflow` and hands the outcome to
// `CarePathwayTimeline`. Flutter still cannot: `fetchAppointmentWorkflow` returns null there and its TimelineTab
// discards the response to draw a hardcoded 2018 list (`timeline_tab.dart:38-79`,
// `encounter_data_source_impl.dart:1116`) — the design's six rows ARE that mock, and it stays out of app code.
// Nothing below ever falls back to a fixture, and the four outcomes a clinician can be looking at
// — still loading, load failed, no pathway wired, a pathway with nothing on it — stay four
// distinct inputs to the section (`loading`, `error`, `activities === undefined`, `[]`), because
// reading any of the other three as "this patient has no care-pathway activity" is a clinical
// misread (review #314).
//
// ⚠️ NEVER point this at `workflows/appointments/{id}` without `/history`. Same shape, but that
// route RUNS the workflow — invoices and finalizes. See `@/types/workflow-api` and the header of
// `use-appointment-workflow.ts`.
import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { toCarePathwayActivities, type CarePathwayActivity } from './lib/care-pathway';
import { useAppointmentWorkflow } from './services/use-appointment-workflow';
import { CarePathwayTimeline } from './care-pathway-timeline';
import { PanelSection } from './panel-section';

/** The panels the DS rail named as tabs. Kept as a type so a host's tab state still compiles; the
 *  column no longer switches between them (every panel is a section, see the header). */
export type CarePathwayTab = 'timeline' | 'note' | 'orders' | 'edit';

export interface CarePathwayFeedProps {
  /**
   * Appointment whose care-pathway history to load (`useAppointmentWorkflow` → GET
   * workflows/appointments/{id}/history). Absent → no request is made and the section says so,
   * which is what an unwired host (a story, a deep-link before the appointment resolves) should show.
   */
  appointmentId?: string;
  /**
   * A feed supplied by the host, which WINS over the fetch and disables it — so passing both
   * cannot leave two sources racing for the same list. This is how stories and tests drive the
   * populated states; app hosts pass `appointmentId` and let the feed fetch.
   *
   * Empty array ≠ absent: `[]` is "this appointment's pathway has no activities", `undefined`
   * is "no feed" — the section renders them as different lines.
   */
  activities?: CarePathwayActivity[];
  /** Day label beside the section title ("Today"). */
  dayLabel?: string;
}

/** The data container: owns the /history read and hands its outcome to `CarePathwayTimeline`. */
export function CarePathwayFeed({ appointmentId, activities, dayLabel }: CarePathwayFeedProps) {
  const query = useAppointmentWorkflow({ appointmentId, enabled: activities === undefined });
  const fetched = useMemo(() => toCarePathwayActivities(query.data?.data), [query.data]);

  // The four outcomes, kept apart. With no appointment id and no host list NOTHING was asked
  // for (`undefined` → "isn't available yet"); an appointment whose read has not succeeded is
  // an empty list only once it is neither loading nor failed — `loading` and `error` win in the
  // section. `isLoading` (not `isPending`): with no appointmentId the query is disabled and would
  // sit in `pending` forever.
  const source = activities ?? (appointmentId ? (query.isSuccess ? fetched : []) : undefined);

  // DEV-ONLY FOOTGUN GUARD (review #402). Supplying BOTH props is almost always a mistake: the
  // override wins, so the fetch is skipped and a stale list shadows the live feed with no
  // visible symptom — the section looks perfectly healthy while showing the wrong pathway.
  //
  // Warns, never throws, and does NOT change precedence: `activities` winning is the documented
  // contract and every story depends on it. Deliberately logs no appointment id or row content —
  // it is a wiring mistake, and the fix needs neither.
  const warnedDualFeed = useRef(false);
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (!appointmentId || activities === undefined || warnedDualFeed.current) return;
    warnedDualFeed.current = true;
    console.warn(
      '[care-pathway] CarePathwayRail was given BOTH `appointmentId` and an `activities` ' +
        'override. The override wins and the /history read is SKIPPED — pass one or the ' +
        'other. (PROD-738)',
    );
  }, [appointmentId, activities]);

  return (
    <CarePathwayTimeline
      activities={source}
      loading={query.isLoading}
      // A failed read must NOT collapse into "no activity", and it must be recoverable in place.
      error={query.isError}
      onRetry={() => void query.refetch()}
      dayLabel={dayLabel}
    />
  );
}

export interface CarePathwayRailContainerProps extends CarePathwayFeedProps {
  /** No longer read: the column has no tabs (every panel is a section). Kept so hosts compile. */
  activeTab?: CarePathwayTab;
  /** No longer read, as `activeTab`. */
  onTabChange?: (tab: CarePathwayTab) => void;
  /**
   * The generated note (PROD-1988), drawn as the column's Note section. Absent → no Note section.
   *
   * Flutter mounts the note beside the chart AND beside the pre-encounter workflow: `sidePanel()`
   * (`encounter_screen.dart:1657`) is a sibling of both `tabViewInitial` and the started
   * encounter's column, with GeneratedNotesWidget as its first tab.
   */
  note?: ReactNode;
}

/** The pre-encounter right column: Care pathway, then the Note, as collapsible sections. */
export function CarePathwayRailContainer({ appointmentId, activities, dayLabel, note }: CarePathwayRailContainerProps) {
  return (
    <aside aria-label="Encounter panel" data-slot="encounter-right-panel" className="enc-right-panel">
      <CarePathwayFeed appointmentId={appointmentId} activities={activities} dayLabel={dayLabel} />
      {note ? (
        // Before Start the note holds no answers yet (the identity stamp, the reason, empty step
        // lines), so it starts closed, like every section with nothing in it yet.
        <PanelSection id="note" title="Note" defaultOpen={false}>
          {note}
        </PanelSection>
      ) : null}
    </aside>
  );
}
